import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable import/no-cycle */
/* eslint-disable consistent-return */
import { useContext, useEffect, useMemo, useState } from "react";
import { areObjectsEqualAsStrings } from "Helpers/util";
import NavBar from "ProfileComponents/Navigation/NavBar";
import toastService from "Root/profile/toastService";
import Loading from "Components/Loading";
import { useDispatch, useSelector } from "react-redux";
import { setGlobalLoading, setUserAccountAction, setCommunicationPrefsAction, setSettingsStoreAction, } from "Root/profile/redux/actionCreators/makeActionCreator";
import { fetchUserAccountThunk, fetchCommunicationPrefsThunk, } from "Root/profile/redux/thunkCreators/accountSettings/accountSettingsThunk";
import { validateEmail } from "Root/quiz/components/emailSignUp/emailSignUp";
import { apiUpdateAccountData } from "Root/api/apiProfile";
import { useTracking } from "react-tracking";
import { SubscriptionV2 } from "Root/profile/utils/trackingEnums";
import { useLocation } from "react-router";
import ShippingAddress from "./ShippingAddress";
import Separator from "../common/Separator";
import styles from "./styles.module.css";
import CommunicationPreferences from "./CommunicationPreferences";
import PaymentMethod from "./PaymentMethod";
import AccountDetails from "./AccountDetails";
import AccountContext from "./context";
import BottomBar from "../common/BottomBar";
import UnderlinedLink from "../common/UnderlinedLink";
import BillingAddress from "./BillingAddress";
import TranslucentNavbar from "../common/TranslucentNavbar/TranslucentNavbar";
import { useScreenWidth } from "../SubscriptionPageV2/hooks";
import GiftCardSection from "./GiftCardSection";
import H2Font from "../common/H2Font";
const AccountSettings = () => {
    const { nav_visibility } = useSelector((state) => state.components);
    const reduxStoreIsEmpty = useSelector((store) => store.checkStore.settings);
    const user = useSelector((state) => state.user);
    const loading = useSelector((state) => state.accountSettings.loading);
    const userAccount = useSelector((state) => state.accountSettings.userAccount);
    const originalUserAccountData = useSelector((state) => state.accountSettings.originalUserAccountData);
    const communicationPrefs = useSelector((state) => state.accountSettings.communicationPrefs);
    const context = useContext(AccountContext);
    const { hash } = useLocation();
    const [isVerified, setIsVerified] = useState(true);
    const [editMode, setEditMode] = useState({
        details: false,
        shipAddress: false,
        payment: false,
        communication: false,
        billingAddress: false,
        giftCards: false,
    });
    const dispatch = useDispatch();
    const isMobile = useScreenWidth() < 768;
    const updateEditModeState = (value, name) => {
        setEditMode({ ...editMode, [name]: value });
    };
    const clearEditModeState = () => {
        setEditMode({
            details: false,
            shipAddress: false,
            payment: false,
            communication: false,
            billingAddress: false,
            giftCards: false,
        });
    };
    const onChangeData = (value, name, objName) => {
        if (objName) {
            dispatch(setUserAccountAction({
                ...userAccount,
                [objName]: {
                    ...userAccount[objName],
                    [name]: value,
                },
            }));
        }
        else {
            dispatch(setUserAccountAction({
                ...userAccount,
                [name]: value,
            }));
        }
    };
    const onChangeCommunicationData = (value, name) => {
        dispatch(setCommunicationPrefsAction({ ...communicationPrefs, [name]: value }));
    };
    const updateAccountData = async () => {
        if (!userAccount.firstName ||
            !userAccount.email ||
            !userAccount.shipping_data.firstName ||
            !userAccount.shipping_data.address ||
            !userAccount.shipping_data.city ||
            !userAccount.shipping_data.state ||
            !userAccount.shipping_data.zip) {
            return toastService.errorMessage("Shipping address fields are required");
        }
        if (!userAccount.billing_data.firstName ||
            !userAccount.billing_data.city ||
            !userAccount.billing_data.address ||
            !userAccount.billing_data.state ||
            !userAccount.billing_data.zip) {
            return toastService.errorMessage("Billing address fields are required");
        }
        if (!validateEmail(userAccount.email)) {
            return toastService.errorMessage("Please enter a valid email");
        }
        try {
            dispatch(setGlobalLoading(true));
            await apiUpdateAccountData({ ...userAccount, verify: isVerified });
            dispatch(fetchUserAccountThunk());
            clearEditModeState();
            toastService.successMessage("Account data updated successfully ");
        }
        catch (error) {
            const _error = error;
            toastService.handleError(_error);
        }
        finally {
            dispatch(setGlobalLoading(false));
        }
    };
    useEffect(() => {
        if (reduxStoreIsEmpty) {
            dispatch(fetchCommunicationPrefsThunk());
            dispatch(fetchUserAccountThunk());
            dispatch(setSettingsStoreAction(false));
        }
        window.scrollTo(0, 0);
    }, []);
    const contextValue = useMemo(() => ({
        ...context,
        editMode,
        updateEditModeState,
        userAccount,
        onChangeData,
        onChangeCommunicationData,
        communicationPrefs,
    }), [editMode, updateEditModeState, userAccount, onChangeData, onChangeCommunicationData, communicationPrefs]);
    const { trackEvent } = useTracking();
    const trackCancelSubscription = () => {
        trackEvent({
            funComponent: SubscriptionV2.Subscription,
            event: `${SubscriptionV2.Cancel_subscription}__page=settings`,
        });
    };
    useEffect(() => {
        if (!loading && hash && document.getElementById(hash.substr(1))) {
            // Check if there is a hash and if an element with that id exists
            document.getElementById(hash.substr(1))?.scrollIntoView({ behavior: "smooth", block: "center" });
            if (hash === "#paymentMethod") {
                updateEditModeState(true, "payment");
            }
        }
    }, [hash, loading]);
    useEffect(() => {
        if (areObjectsEqualAsStrings(userAccount, originalUserAccountData)) {
            const launcher = document.getElementById("launcher");
            if (launcher) {
                launcher.style.zIndex = "100";
            }
        }
    }, [userAccount, originalUserAccountData]);
    return (_jsxs(AccountContext.Provider, { value: contextValue, children: [nav_visibility && (_jsx(NavBar, { userName: user.full_name, showTop: true, userLoggedIn: true, hasSubscription: user.has_subscription, bannerData: user.banner_data })), _jsx("div", { className: styles.account_page, children: loading ? (_jsxs(_Fragment, { children: [_jsx(Loading, {}), " ", _jsx(Loading, {}), " ", _jsx(Loading, {})] })) : (_jsxs(_Fragment, { children: [isMobile && (_jsx(TranslucentNavbar, { animated: true, children: _jsx("h4", { children: "Account Settings" }) })), _jsxs("div", { children: [_jsx("div", { className: "my-profile__title", children: _jsx(H2Font, { children: "Account Settings" }) }), _jsx(Separator, {}), _jsx(AccountDetails, {}), _jsx(Separator, {}), _jsx(ShippingAddress, {}), _jsx(Separator, {}), _jsx(PaymentMethod, {}), _jsx(Separator, {}), _jsx(BillingAddress, {}), _jsx(Separator, {}), _jsx(GiftCardSection, {}), _jsx(Separator, {}), _jsx(CommunicationPreferences, {}), user.has_subscription && (_jsx(UnderlinedLink, { linkTo: "/app/cancel-subscription-v2/", linkText: "Cancel My Subscription", trackingText: "settings-cancel-button", onButtonClick: trackCancelSubscription })), !areObjectsEqualAsStrings(userAccount, originalUserAccountData) && (_jsx(BottomBar, { cancelLabel: "Cancel", onCancel: () => dispatch(setUserAccountAction({ ...originalUserAccountData })), submitLabel: "Save changes", type: "button", onSave: updateAccountData, isRounded: true }))] })] })) })] }));
};
export default AccountSettings;
